<template>
  <div class="auth-page">
    <div class="auth-card">
      <div class="auth-head">
        <h1>{{ mode === 'login' ? '登录' : '访客注册' }}</h1>
        <p>{{ mode === 'login' ? '输入账号和密码后进入空间' : '注册后将自动以访客身份进入空间' }}</p>
      </div>

      <div class="auth-switch">
        <button type="button" class="auth-switch-btn" :class="{ active: mode === 'login' }" @click="mode = 'login'">登录</button>
        <button type="button" class="auth-switch-btn" :class="{ active: mode === 'register' }" @click="mode = 'register'">访客注册</button>
      </div>

      <div class="auth-form" @keyup.enter="submitLogin">
        <div class="auth-field">
          <label>账号</label>
          <el-input v-model="form.username" placeholder="请输入用户名" />
        </div>

        <div class="auth-field">
          <label>密码</label>
          <el-input v-model="form.password" placeholder="请输入密码" show-password />
        </div>

        <div v-if="mode === 'login'" class="auth-remember-row">
          <el-checkbox v-model="rememberMe">记住我</el-checkbox>
        </div>

        <template v-if="mode === 'register'">
          <div class="auth-field">
            <label>昵称</label>
            <el-input v-model="registerForm.nickname" placeholder="请输入昵称" />
          </div>

          <div class="auth-grid">
            <div class="auth-field">
              <label>邮箱</label>
              <el-input v-model="registerForm.email" placeholder="选填" />
            </div>

            <div class="auth-field">
              <label>手机号</label>
              <el-input v-model="registerForm.phone" placeholder="选填" />
            </div>
          </div>

          <div class="auth-field">
            <label>生日</label>
            <el-date-picker
              v-model="registerForm.birthday"
              class="auth-date-picker"
              popper-class="auth-date-popper"
              type="date"
              format="YYYY / MM / DD"
              value-format="YYYY-MM-DD"
              placeholder="请选择生日"
              style="width: 100%;"
            />
          </div>
        </template>

        <el-button type="danger" class="auth-submit" :loading="submitting" @click="submitLogin">{{ mode === 'login' ? '登录' : '注册并进入' }}</el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { useRoute, useRouter } from 'vue-router'
import { clearRememberedLoginForm, getRememberLoginEnabled, getRememberedLoginForm, persistAuth, syncRememberedLoginForm } from '../utils/auth'
import { rawRequest } from '../utils/http'

const route = useRoute()
const router = useRouter()
const submitting = ref(false)
const mode = ref('login')
const rememberMe = ref(getRememberLoginEnabled())

const form = reactive({
  username: '',
  password: ''
})

const registerForm = reactive({
  nickname: '',
  email: '',
  phone: '',
  birthday: ''
})

const getWelcomeCopy = (role, action) => {
  const isVisitor = role === 'visitor'

  if (action === 'register') {
    return isVisitor ? '注册成功~欢迎到访~' : '注册成功~爱你宝宝~'
  }

  return isVisitor ? '登录成功~欢迎到访~' : '登录成功~爱你宝宝~'
}

const submitLogin = async () => {
  if (!form.username.trim()) {
    ElMessage.warning('请输入账号')
    return
  }

  if (!form.password.trim()) {
    ElMessage.warning('请输入密码')
    return
  }

  if (mode.value === 'register' && !registerForm.nickname.trim()) {
    ElMessage.warning('请输入昵称')
    return
  }

  submitting.value = true

  try {
    const response = await rawRequest(mode.value === 'login' ? '/api/auth/login' : '/api/visitor/register', {
      method: 'POST',
      auth: false,
      body: mode.value === 'login'
        ? {
            username: form.username.trim(),
            password: form.password
          }
        : {
            username: form.username.trim(),
            password: form.password,
            nickname: registerForm.nickname.trim(),
            email: registerForm.email.trim() || undefined,
            phone: registerForm.phone.trim() || undefined,
            birthday: registerForm.birthday || undefined
          }
    })

    const contentType = response.headers.get('content-type') || ''
    const result = contentType.includes('application/json') ? await response.json() : null

    if (response.status === 404) {
      ElMessage.error('接口返回 404，请先确认前端开发服务已重启，并检查后端是否存在对应路由')
      return
    }

    if (!response.ok || result?.code !== 0 || !result?.data?.token) {
      ElMessage.error(result.msg || (mode.value === 'login' ? '登录失败，请检查账号或密码' : '注册失败，请检查输入信息'))
      return
    }

    persistAuth(result.data)

    if (mode.value === 'login') {
      syncRememberedLoginForm(rememberMe.value, {
        username: form.username.trim(),
        password: form.password
      })
    } else {
      clearRememberedLoginForm()
    }

    ElMessage.success(getWelcomeCopy(result.data.user?.role, mode.value))
    sessionStorage.setItem('us_space_welcome_popup', result.data.user?.role === 'visitor' ? 'visitor' : 'member')
    await router.push(typeof route.query.redirect === 'string' && route.query.redirect ? route.query.redirect : '/stories')
  } catch (error) {
    ElMessage.error(error?.message || (mode.value === 'login' ? '登录请求失败，请确认后端服务和接口联通正常' : '注册请求失败，请确认后端服务和接口联通正常'))
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  const remembered = getRememberedLoginForm()
  if (rememberMe.value && remembered) {
    form.username = remembered.username || ''
    form.password = remembered.password || ''
  }

  const expiredMessage = sessionStorage.getItem('us_space_auth_expired')
  if (expiredMessage) {
    sessionStorage.removeItem('us_space_auth_expired')
    ElMessage.warning(expiredMessage)
  }
})
</script>
